import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Typography, Box, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import Question from "../../components/Question";
import ImageUpload from "../../components/ImageUpload";
import ContinueButton from "../../components/ContinueButton";
import { continueButtonSx } from "../../lib/uiformatting";
import { setNewPhotos } from "../../redux/userSlice";
import { MinorUserContext } from "../../context/UserNameProvider";
import { callBackendApiPostFormData } from "../../lib/api";
import setPageAndDeferNavigate from "../../lib/setPageAndDeferNavigate";
import logDrtEvent from '../../lib/logging';
import { alertOps } from "../../lib/api";

export default function RecentPhoto(props) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { isMinor, firstName } = useContext(MinorUserContext);
    const user = useSelector((state) => state.user);
    const uploadedPhotos = useSelector((state) => state.user.newPhotos);
    const caseID = useSelector((state) => state.user.caseID);
    const patientID = useSelector((state) => state.user.patientID);
    const questionsForDoctor = useSelector((state) => state.user.questionsForDoctor);

    const [files, setFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [followUpCare, setFollowUpCare] = useState([]);

    useEffect(() => {
        const followUpArray = Array.isArray(user.followUpCare)
            ? user.followUpCare
            : typeof user.followUpCare === "string"
                ? [user.followUpCare]
                : [];
        setFollowUpCare(followUpArray);
    }, [user.followUpCare]);

    async function handleContinue() {
        if (files.length < 3) return;

        setIsUploading(true);

        try {
            const formData = new FormData();
            formData.append("caseID", caseID);
            formData.append("patientID", patientID);
            files.forEach(file => {
                formData.append('photos', file);
            });

            logDrtEvent(`[returning] Image upload started for patient: ${patientID} and case: ${caseID}`);

            const response = await callBackendApiPostFormData("/api/v1/upload-images", formData);
            if (!response.ok) {
                await alertOps(user, "Image upload failed on returning intake", `Image upload failed with status: ${response.status}`, "Image upload");
                logDrtEvent(`Image upload failed with status: ${response.status}`);
                if (response.status === 404) {
                    logDrtEvent(`Image upload failed with status: ${response.status}, due to missing caseID or patientID`);
                    navigate("/error", { state: { returning: true } });
                }
                throw new Error("Image upload failed");
            }

            const data = await response.json();
            if (data.pre_signed_urls) {
                dispatch(setNewPhotos(data.pre_signed_urls));
                // TBD - for future self pay only state flows
                // if (user.address?.state === "XY") {
                //     dispatch(updatePayMethod("self"));
                //     dispatch(insuranceReset());
                //     setPageAndDeferNavigate(dispatch, '/payment', () =>
                //         navigate('/payment', { state: { returning: true } })
                //     );
                // } else {
                setPageAndDeferNavigate(dispatch, '/insurance-options', () =>
                    navigate('/insurance-options', { state: { returning: true } })
                );
                // }
            }
        } catch (err) {
            logDrtEvent(`Error uploading images with error: ${err}`);
            await alertOps(user, "Image upload failed on returning intake", `Image upload failed with error: ${err}`, "Image upload");
            navigate("/error", { state: { returning: true } });
        } finally {
            setIsUploading(false);
        }
    }

    const handleBack = () => {
        if (user.sex.sexAtBirth === "Female" || user.sex.sexAtBirth === "Prefer not to answer") {
            setPageAndDeferNavigate(dispatch, '/maternity', () =>
                navigate('/maternity', { state: { returning: true } })
            );
        } else if (user.isIsotretinoin && (user.address.state === 'CT' || user.address.state === 'MA') && (user.pickedMedication === "yes" && user.onPrescribedMedication === "yes")) {
            if (user.mentalHealthTrigger) {
                setPageAndDeferNavigate(dispatch, '/phq9-questionnaire', () =>
                    navigate('/phq9-questionnaire', { state: { returning: true } })
                );
            } else {
                setPageAndDeferNavigate(dispatch, '/side-effects-checklist', () =>
                    navigate('/side-effects-checklist', { state: { returning: true } })
                );
            }
        } else if (questionsForDoctor) {
            setPageAndDeferNavigate(dispatch, '/questions-for-doctor', () =>
                navigate('/questions-for-doctor', { state: { returning: true } })
            );
        }
        else if (followUpCare.includes("I have questions for the provider") ||
            followUpCare.includes("I have another reason not listed above")
        ) {
            setPageAndDeferNavigate(dispatch, '/follow-up-care', () =>
                navigate('/follow-up-care', { state: { returning: true } })
            );
        } else {
            setPageAndDeferNavigate(dispatch, '/picked-medication', () =>
                navigate('/picked-medication', { state: { returning: true } })
            );
        }
    };

    return (
        <Question
            onBack={handleBack}
            title={"Please share a recent photo of your skin issue"}
            returning={true}
            subtitle={
                <>
                    <Typography variant="body1" sx={{ mb: "1rem" }}>
                        Your dermatologist has requested <strong>3 photos</strong> of the area of concern.
                        <strong> One should be close-up (~5 inches) and one about 10 inches away.</strong>
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        <Accordion sx={{ maxWidth: '900px', width: '100%' }}>
                            <AccordionSummary
                                aria-controls="panel1a-content"
                                id="panel1a-header"
                                sx={{ pb: 0, pt: 0 }}
                                expandIcon={<ExpandMoreIcon />}
                            >
                                <Typography>
                                    Here are some tips for taking photos for the doctor
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0, textAlign: 'left', pb: 0 }}>
                                <ul style={{ paddingInlineStart: '20px' }}>
                                    <li>Make sure the area is in focus.</li>
                                    <li>Make sure the area is well-lit.</li>
                                </ul>
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                    <Typography variant="body1" sx={{ mt: "1rem" }}>
                        {isMinor ? (firstName + "'s") : "Your"} photos are confidential and will only be seen by {isMinor ? (firstName + "'s") : "your"} provider. Please focus on <strong>one skin issue</strong>.
                    </Typography>
                </>
            }
            input={
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        width: '100%',
                        maxWidth: '500px',
                        margin: '0 auto',
                        gap: 2
                    }}
                >
                    <ImageUpload
                        multiple
                        files={files}
                        setFiles={setFiles}
                        uploadedUrls={uploadedPhotos}
                        sx={{ width: '100%' }}
                    />
                </Box>
            }
            footer={
                <Box
                    sx={{
                        ...continueButtonSx,
                        flexDirection: "column",
                        alignItems: "center",
                        width: '100%',
                        maxWidth: '900px',
                        margin: '0 auto',
                        position: "sticky",
                        bottom: 0,
                    }}
                >
                    <ContinueButton
                        onClick={handleContinue}
                        disabled={files.length < 3 || isUploading}
                        title={isUploading ? "Uploading..." : "Continue"}
                        selected={files.length >= 3}
                    />
                    <Typography
                        variant="body2"
                        color={files.length >= 3 ? "text.primary" : "text.secondary"}
                        sx={{
                            fontWeight: 'bold',
                            textAlign: "center",
                            mt: 1,
                            mb: 1.5
                        }}
                    >
                        {files.length}/3 images uploaded
                    </Typography>
                </Box>
            }
        />
    );
}
